import env from '@tcc/env-detect';
import { hashHistory } from 'react-router';
import { queryParam, toQueryString } from './queryString';
import { redirectTo } from './redirectTo';

type RedirectOptions = object;

export function redirect(url: string): void {
  window.location = url as string & Location;
}

/**
 * Returns the API URL for a given path. If no rootUrl is given then use the
 * envDetect getServiceHostname
 */
export function getApiUrl(path: string, rootUrl?: string): string {
  const hostname =
    typeof rootUrl === 'string' ? rootUrl : env.getServiceHostname();
  return `${hostname}${path}`;
}

/**
 * Redirect to the specified url with all options in the query string
 */
export function redirectWithOptions(
  url: string,
  options: RedirectOptions = {},
  hash?: string
): void {
  redirect(`${url}?${toQueryString(options)}${hash || ''}`);
}

/**
 * Redirect to the specified url with the client-id if present and specified options
 */
export function redirectWithClientId(
  url: string,
  options: RedirectOptions = {},
  hash?: string
): void {
  redirectWithOptions(
    url,
    {
      ...options,
      'client-id': queryParam('client-id'),
    },
    hash
  );
}

interface IdsFromHash {
  fieldId: string;
  mapLayerIds: string[];
  seasonIds: string[];
}

export function getIdsFromHash(): IdsFromHash {
  const { pathname } = hashHistory.getCurrentLocation();
  const urlParams = pathname.replace('/dmb', '').split('/');
  const fieldId = urlParams.length > 1 ? urlParams[1] : '';
  const seasonIds = urlParams.length > 2 ? urlParams[2].split('|') : [''];
  const mapLayerIds = urlParams.length > 3 ? urlParams[3].split('|') : [''];

  return { fieldId, mapLayerIds, seasonIds };
}

export function getAppPath(): string {
  return '/dmb/';
}

/**
 * Replace a session or layer on the given position.
 */
function replaceId(ids: string[], newId: string, index: number): string[] {
  const result = ids.map((element, position) =>
    index === position ? newId : element
  );
  if (result.length <= index) {
    result[index] = newId;
  }
  return result;
}

/**
 * Redirect to split view with the specified season and layer and view
 */
export function switchView(
  index: number,
  season: string,
  seasonIds: string[],
  layer: string,
  layerIds: string[],
  fieldId: string
): void {
  const newSeasonIds = replaceId(seasonIds, season, index).join('|');
  const newLayerIds = replaceId(layerIds, layer, index).join('|');

  redirectTo(`${getAppPath()}${fieldId}/${newSeasonIds}/${newLayerIds}`)
}

/**
 * Close Split View and leave view with specified season and layer.
 */
export function closeSplitView(season: string, layer: string, fieldId: string): void {
  redirectTo(`${getAppPath()}${fieldId}/${season}/${layer}`)
}

/**
 * Switch the layer of a given view.
 */
export function switchLayer(index: number, layer: string): void {
  const { fieldId, mapLayerIds, seasonIds } = getIdsFromHash();
  const layers = replaceId(mapLayerIds, layer, index).join('|');
  redirectTo(`${getAppPath()}${fieldId}/${seasonIds.join('|')}/${layers}`)
}

/**
 * Switch the field
 */
export function switchField(field: string): void {
  const clientId = queryParam('client-id');
  const clientIdSearch = clientId ? `?client-id=${clientId}` : '';
  redirectTo(`${getAppPath()}${field}`, true, clientIdSearch)
}
